import { supabase } from './supabase';

export type OfferteContext = 'verzekering' | 'b2b' | 'consumer';

export interface SendOffertePdfInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body?: string;
  context?: OfferteContext;
  packageSku?: string;
  note?: string;
}

export interface SendOffertePdfResponse {
  success: boolean;
  contentDocumentId: string | null;
  contentVersionId?: string | null;
  resendId: string | null;
  to: string[];
}

export type DeliveryStatus =
  | 'sent'
  | 'delivery_delayed'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'complained'
  | 'failed';

export interface SentEmailDeliveryEvent {
  type: string;
  at: string;
  data?: unknown;
}

export interface SentEmail {
  id: string;
  createdAt: string;
  completedAt: string | null;
  actorEmail: string;
  to: string[];
  cc: string[];
  subject: string | null;
  status: 'pending' | 'success' | 'error';
  errorCode: string | null;
  errorMessage: string | null;
  resendId: string | null;
  deliveryStatus: DeliveryStatus | null;
  deliveryEvents: SentEmailDeliveryEvent[];
  contentVersionId: string | null;
  contentDocumentId: string | null;
  filename: string | null;
  fileSize: number | null;
}

/** Eén touch uit de automatische offerte-contactreeks (eigen infra). */
export interface ReeksEntry {
  id: number;
  mailType: string; // Q01 | SMS02 | FU01 | FU02 | FU03
  to: string | null;
  status: 'sent' | 'failed' | 'suppressed' | 'skipped';
  error: string | null;
  body: string | null;
  sentAt: string;
}

export interface SentEmailsResult {
  entries: SentEmail[];
  reeks: ReeksEntry[];
}

async function authToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Geen actieve sessie');
  return session.access_token;
}

/**
 * Download de offerte-PDF in de browser via een tijdelijke <a download>.
 * Triggert daadwerkelijk het bestand-opslaan dialog van de browser.
 */
export async function downloadOffertePdf(
  leadId: string,
  options?: { context?: OfferteContext; packageSku?: string; note?: string },
): Promise<void> {
  const token = await authToken();
  const params = new URLSearchParams({ id: leadId });
  if (options?.context) params.set('context', options.context);
  if (options?.packageSku) params.set('packageSku', options.packageSku);
  if (options?.note && options.note.trim()) params.set('note', options.note);
  const response = await fetch(`/api/offertes/pdf?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'PDF-download mislukt' }));
    throw new Error(err.error || err.detail || 'PDF-download mislukt');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Gebruik filename uit Content-Disposition als die er is, anders fallback.
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  a.download = match?.[1] ?? `offerte-${leadId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Open de offerte-PDF in een nieuw tabblad voor preview.
 */
export async function previewOffertePdf(
  leadId: string,
  options?: { context?: OfferteContext; packageSku?: string; note?: string },
): Promise<void> {
  const token = await authToken();
  const params = new URLSearchParams({ id: leadId, inline: '1' });
  if (options?.context) params.set('context', options.context);
  if (options?.packageSku) params.set('packageSku', options.packageSku);
  if (options?.note && options.note.trim()) params.set('note', options.note);
  const response = await fetch(
    `/api/offertes/pdf?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'PDF-preview mislukt' }));
    throw new Error(err.error || err.detail || 'PDF-preview mislukt');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // URL niet meteen revoken — Safari heeft tijd nodig om het tabblad te laden.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Verstuur de offerte-PDF per email via Resend (incl. archivering als ContentVersion
 * aan de Lead in Salesforce).
 */
export async function sendOffertePdf(
  leadId: string,
  input: SendOffertePdfInput,
): Promise<SendOffertePdfResponse> {
  const token = await authToken();
  const response = await fetch(
    `/api/offertes/pdf-send?id=${encodeURIComponent(leadId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'PDF-mail verzenden mislukt' }));
    throw new Error(err.error || err.errorMessage || 'PDF-mail verzenden mislukt');
  }
  return response.json() as Promise<SendOffertePdfResponse>;
}

/**
 * Haal de verzonden-PDF-historie voor deze lead op (audit-log + Resend delivery
 * events). Sorteert op nieuwste eerst.
 */
export async function fetchSentEmails(leadId: string): Promise<SentEmailsResult> {
  const token = await authToken();
  const response = await fetch(
    `/api/offertes/sent-emails?leadId=${encodeURIComponent(leadId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Verzonden mails ophalen mislukt' }));
    throw new Error(err.error || err.detail || 'Verzonden mails ophalen mislukt');
  }
  const data = (await response.json()) as { entries: SentEmail[]; reeks?: ReeksEntry[] };
  return { entries: data.entries ?? [], reeks: data.reeks ?? [] };
}

/**
 * Open de gearchiveerde PDF van een eerder verstuurde offerte in een nieuw tabblad.
 * Streamt uit Salesforce ContentVersion via een server-side proxy zodat de browser
 * geen SF-login nodig heeft.
 */
export async function openArchivedPdf(contentVersionId: string): Promise<void> {
  const token = await authToken();
  const response = await fetch(
    `/api/offertes/sent-pdf?contentVersionId=${encodeURIComponent(contentVersionId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Archief-PDF ophalen mislukt' }));
    throw new Error(err.error || err.detail || 'Archief-PDF ophalen mislukt');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
